import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import type { ReactNode, Ref } from 'react';
import { useId, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Collapse } from '#src/atoms/layout/Collapse/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LinkBox, LinkOverlay } from '#src/atoms/navigation/Box/index.tsx';
import { glossary } from '#src/lib/glossary.ts';

type Props = {
  title: ReactNode;
  children: ReactNode | ((props: { expanded: boolean }) => ReactNode);
  startsOpen?: boolean;
  noBorder?: boolean;
  noBorderWhenExpanded?: boolean;
  noHorizontalPadding?: boolean;
  onExpandChange?: (expand: boolean) => void;
  isFilter?: boolean;
  ref?: Ref<HTMLDivElement>;
};

type BaseAccordionProps = {
  expanded: boolean;
} & Omit<Props, 'startsOpen'>;

const Root = styled(Vertical)`
  color: var(--c-white);
  &:not(.no-border) {
    border-bottom: 1px solid var(--c-nd-50);
  }
`;

const StyledLinkBox = styled(LinkBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--double-unit);
  padding: var(--intermediate-unit) var(--double-unit);
  .no-horizontal-padding & {
    padding: var(--intermediate-unit) 0;
  }
  .is-filter & {
    transition: all 0.3s;
    &:hover {
      background-color: var(--c-nd-100);
    }
  }
`;

const Details = styled.div`
  --padding-top: 0;
  padding: var(--padding-top) var(--double-unit) var(--double-unit);
  .no-title & {
    --padding-top: var(--intermediate-unit);
  }
  .no-horizontal-padding & {
    padding: var(--padding-top) 0 var(--double-unit);
  }
  .is-filter & {
    padding: var(--double-unit);
  }
`;

const Icon = styled(FontAwesomeIcon)`
  color: var(--c-nd-600-opaque);
  fill: var(--c-nd-600);
  transform: rotate(0deg);
  transition: 0.3s transform;
  .expanded & {
    transform: rotate(-180deg);
  }
`;

export const BaseAccordion = ({
  title,
  expanded,
  noBorder,
  noBorderWhenExpanded,
  noHorizontalPadding,
  children,
  onExpandChange,
  isFilter = false,
  ref,
}: BaseAccordionProps) => {
  const { formatMessage } = useIntl();
  const id = useId();

  return (
    <Root
      gap={0}
      ref={ref}
      className={classnames({
        'no-border': (noBorderWhenExpanded && !expanded) || noBorder,
        'is-filter': isFilter,
        'no-horizontal-padding': noHorizontalPadding,
        expanded,
      })}
    >
      <StyledLinkBox id={id}>
        {title}
        <LinkOverlay
          as="button"
          aria-labelledby={id}
          onClick={() => {
            onExpandChange?.(!expanded);
          }}
        >
          <Icon
            icon={faChevronDown}
            height={16}
            aria-label={formatMessage(
              expanded ? glossary.showLess : glossary.showMore
            )}
          />
        </LinkOverlay>
      </StyledLinkBox>
      <Collapse open={expanded} gap={0}>
        <Details>
          {typeof children === 'function' ? children({ expanded }) : children}
        </Details>
      </Collapse>
    </Root>
  );
};

export const Accordion = ({
  title,
  startsOpen = false,
  noBorder,
  noBorderWhenExpanded,
  noHorizontalPadding,
  children,
  onExpandChange,
  isFilter = false,
  ref,
}: Props) => {
  const [expanded, setExpanded] = useState(startsOpen);

  return (
    <BaseAccordion
      title={title}
      isFilter={isFilter}
      expanded={expanded}
      noBorder={noBorder}
      noBorderWhenExpanded={noBorderWhenExpanded}
      noHorizontalPadding={noHorizontalPadding}
      ref={ref}
      onExpandChange={() => {
        onExpandChange?.(!expanded);
        setExpanded(!expanded);
      }}
    >
      {children}
    </BaseAccordion>
  );
};

export const FilterAccordion = ({
  title,
  children,
  startsOpen,
  noHorizontalPadding,
}: {
  title?: ReactNode;
  children: ReactNode | ((props: { expanded: boolean }) => ReactNode);
  startsOpen?: boolean;
  noHorizontalPadding?: boolean;
}) => {
  if (title) {
    return (
      <Accordion
        title={title}
        startsOpen={startsOpen}
        noBorder
        noBorderWhenExpanded
        isFilter
        noHorizontalPadding={noHorizontalPadding}
      >
        {children}
      </Accordion>
    );
  }

  return (
    <Root gap={0} className="no-title no-border">
      <Details>
        {typeof children === 'function'
          ? children({ expanded: true })
          : children}
      </Details>
    </Root>
  );
};
